import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getMonthGoals, createMonthGoal, getMonthReviews, getMonthCeremonies, createMonthCeremony, extractErrorMessage } from '../services/api';
import { FaPlus, FaCalendarAlt, FaBullseye, FaClipboardList, FaStar } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function MonthPlanning({ user }) {
  const toast = useToast();
  const [goals, setGoals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [ceremonies, setCeremonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('goals');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showCeremonyModal, setShowCeremonyModal] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [savingCeremony, setSavingCeremony] = useState(false);
  const [formErrorGoal, setFormErrorGoal] = useState(null);
  const [formErrorCeremony, setFormErrorCeremony] = useState(null);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [newGoal, setNewGoal] = useState({
    user_id: '',
    month: currentMonth,
    year: currentYear,
    goals: '',
    notes: ''
  });
  const [newCeremony, setNewCeremony] = useState({
    user_id: '',
    month: currentMonth,
    year: currentYear,
    type: '',
    description: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [goalsData, reviewsData, ceremoniesData] = await Promise.all([
        getMonthGoals(),
        getMonthReviews().catch(() => []),
        getMonthCeremonies().catch(() => [])
      ]);
      setGoals(goalsData.goals || goalsData || []);
      setReviews(reviewsData.reviews || reviewsData || []);
      setCeremonies(ceremoniesData.ceremonies || ceremoniesData || []);
    } catch (error) {
      console.error('Error fetching month planning data:', error);
      toast.error(extractErrorMessage(error, 'Monatsplanungsdaten konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    if (savingGoal) return;
    setSavingGoal(true);
    setFormErrorGoal(null);
    try {
      await createMonthGoal(newGoal);
      setNewGoal({ user_id: '', month: currentMonth, year: currentYear, goals: '', notes: '' });
      setShowGoalModal(false);
      toast.success('Monatsziel erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating month goal:', error);
      setFormErrorGoal(extractErrorMessage(error, 'Fehler beim Erstellen des Monatsziels'));
    } finally {
      setSavingGoal(false);
    }
  };

  const handleCreateCeremony = async (e) => {
    e.preventDefault();
    if (savingCeremony) return;
    setSavingCeremony(true);
    setFormErrorCeremony(null);
    try {
      await createMonthCeremony(newCeremony);
      setNewCeremony({ user_id: '', month: currentMonth, year: currentYear, type: '', description: '', notes: '' });
      setShowCeremonyModal(false);
      toast.success('Zeremonie erstellt');
      fetchData();
    } catch (error) {
      console.error('Error creating ceremony:', error);
      setFormErrorCeremony(extractErrorMessage(error, 'Fehler beim Erstellen der Zeremonie'));
    } finally {
      setSavingCeremony(false);
    }
  };

  const closeGoalModal = () => {
    if (savingGoal) return;
    setShowGoalModal(false);
    setFormErrorGoal(null);
  };

  const closeCeremonyModal = () => {
    if (savingCeremony) return;
    setShowCeremonyModal(false);
    setFormErrorCeremony(null);
  };

  const getMonthName = (month) => {
    const names = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return names[month] || month;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1><FaCalendarAlt /> Monatsplanung</h1>
          <div className="header-actions">
            {activeTab === 'goals' && (
              <button className="add-btn" onClick={() => setShowGoalModal(true)}>
                <FaPlus /> Neues Ziel
              </button>
            )}
            {activeTab === 'ceremonies' && (
              <button className="add-btn" onClick={() => setShowCeremonyModal(true)}>
                <FaPlus /> Neue Zeremonie
              </button>
            )}
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab-btn ${activeTab === 'goals' ? 'active' : ''}`} onClick={() => setActiveTab('goals')}>
            <FaBullseye /> Ziele
          </button>
          <button className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
            <FaClipboardList /> Rückblicke
          </button>
          <button className={`tab-btn ${activeTab === 'ceremonies' ? 'active' : ''}`} onClick={() => setActiveTab('ceremonies')}>
            <FaStar /> Zeremonien
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'goals' ? (
          <div className="data-table-container">
            {goals.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Monat</th>
                    <th>Benutzer</th>
                    <th>Ziele</th>
                    <th>Notizen</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {goals.map(goal => (
                    <tr key={goal.id}>
                      <td>{getMonthName(goal.month)} {goal.year}</td>
                      <td>{goal.username || goal.user_id}</td>
                      <td>{goal.goals}</td>
                      <td>{goal.notes || '-'}</td>
                      <td>
                        <span className={`badge ${goal.status === 'completed' ? 'success' : goal.status === 'in_progress' ? 'warning' : ''}`}>
                          {goal.status === 'completed' ? 'Abgeschlossen' : goal.status === 'in_progress' ? 'In Bearbeitung' : goal.status || 'Offen'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Monatsziele vorhanden</div>
            )}
          </div>
        ) : activeTab === 'reviews' ? (
          <div className="data-table-container">
            {reviews.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Monat</th>
                    <th>Benutzer</th>
                    <th>Zusammenfassung</th>
                    <th>Bewertung</th>
                    <th>Erstellt am</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map(review => (
                    <tr key={review.id}>
                      <td>{getMonthName(review.month)} {review.year}</td>
                      <td>{review.username || review.user_id}</td>
                      <td>{review.summary || review.notes || '-'}</td>
                      <td>{review.rating ? `${review.rating}/10` : '-'}</td>
                      <td>{new Date(review.created_at || review.timestamp).toLocaleDateString('de-DE')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Monatsrückblicke vorhanden</div>
            )}
          </div>
        ) : (
          <div className="data-table-container">
            {ceremonies.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Monat</th>
                    <th>Typ</th>
                    <th>Beschreibung</th>
                    <th>Notizen</th>
                    <th>Erstellt am</th>
                  </tr>
                </thead>
                <tbody>
                  {ceremonies.map(ceremony => (
                    <tr key={ceremony.id}>
                      <td>{getMonthName(ceremony.month)} {ceremony.year}</td>
                      <td><span className="badge">{ceremony.type}</span></td>
                      <td>{ceremony.description}</td>
                      <td>{ceremony.notes || '-'}</td>
                      <td>{new Date(ceremony.created_at || ceremony.timestamp).toLocaleDateString('de-DE')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">Keine Zeremonien vorhanden</div>
            )}
          </div>
        )}

        {showGoalModal && (
          <div className="modal-overlay" onClick={closeGoalModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neues Monatsziel</h2>
              {formErrorGoal && <div className="shared-form-error-banner">{formErrorGoal}</div>}
              <form onSubmit={handleCreateGoal}>
                <div className="form-group">
                  <UserSelect
                    value={newGoal.user_id}
                    onChange={id => setNewGoal({ ...newGoal, user_id: id })}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Monat *</label>
                    <select
                      value={newGoal.month}
                      onChange={e => setNewGoal({ ...newGoal, month: parseInt(e.target.value) })}
                      required
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <option key={m} value={m}>{getMonthName(m)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Jahr *</label>
                    <input
                      type="number"
                      value={newGoal.year}
                      onChange={e => setNewGoal({ ...newGoal, year: parseInt(e.target.value) })}
                      min="2020"
                      max="2030"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Ziele *</label>
                  <textarea
                    value={newGoal.goals}
                    onChange={e => setNewGoal({ ...newGoal, goals: e.target.value })}
                    placeholder="Monatsziele beschreiben..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newGoal.notes}
                    onChange={e => setNewGoal({ ...newGoal, notes: e.target.value })}
                    placeholder="Zusätzliche Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeGoalModal} disabled={savingGoal}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingGoal}>
                    {savingGoal ? 'Speichert\u2026' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCeremonyModal && (
          <div className="modal-overlay" onClick={closeCeremonyModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Zeremonie</h2>
              {formErrorCeremony && <div className="shared-form-error-banner">{formErrorCeremony}</div>}
              <form onSubmit={handleCreateCeremony}>
                <div className="form-group">
                  <UserSelect
                    value={newCeremony.user_id}
                    onChange={id => setNewCeremony({ ...newCeremony, user_id: id })}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Monat *</label>
                    <select
                      value={newCeremony.month}
                      onChange={e => setNewCeremony({ ...newCeremony, month: parseInt(e.target.value) })}
                      required
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <option key={m} value={m}>{getMonthName(m)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Jahr *</label>
                    <input
                      type="number"
                      value={newCeremony.year}
                      onChange={e => setNewCeremony({ ...newCeremony, year: parseInt(e.target.value) })}
                      min="2020"
                      max="2030"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Typ *</label>
                  <input
                    type="text"
                    value={newCeremony.type}
                    onChange={e => setNewCeremony({ ...newCeremony, type: e.target.value })}
                    placeholder="z.B. Monatsabschluss, Neubeginn..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung *</label>
                  <textarea
                    value={newCeremony.description}
                    onChange={e => setNewCeremony({ ...newCeremony, description: e.target.value })}
                    placeholder="Beschreibung der Zeremonie..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newCeremony.notes}
                    onChange={e => setNewCeremony({ ...newCeremony, notes: e.target.value })}
                    placeholder="Zusätzliche Notizen..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCeremonyModal} disabled={savingCeremony}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingCeremony}>
                    {savingCeremony ? 'Speichert\u2026' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MonthPlanning;
